/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';

import { PaymentTermsAndConditionsType } from '@amfam/billing/shared/ui';
import { AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, LoadingSpinnerService } from '@amfam/ui-kit';

import { OneTimePaySelectors } from '../../..';
import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';
import { simplifiedPaymentReviewData } from '../../+state/one-time-pay-feature.models';

@Component({
  selector: 'amfam-simplified-payment-confirmation-wrapper',
  templateUrl: './simplified-payment-confirmation-wrapper.component.html',
  styleUrls: ['./simplified-payment-confirmation-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SimplifiedPaymentConfirmationWrapperComponent implements OnInit, OnDestroy {
  simplifiedPaymentInfo$: Observable<simplifiedPaymentReviewData>;
  subHeading: string;
  disclaimer$: Observable<string>;
  primaryButtonName: string;
  secondaryButtonName: string;
  userEmail: string;
  isPhoenix = false;

  // Shared subject for completing observables
  private stop$ = new Subject<void>();

  constructor(
    private dockingBar: DockingBarService,
    private copyService: CopyService,
    private spinner: LoadingSpinnerService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private analyticsFacade: AnalyticsFacade,
    private store: Store
  ) {}
  ngOnInit(): void {
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.simplifiedPayment.dockingBarHeading')
    );
    this.oneTimePayFacade
      .getCustomerEmailAddress()
      .pipe(take(1))
      .subscribe(email => {
        this.userEmail = this.copyService.getCopy(
          `billing.simplifiedPayment.paymentConfirmationEmail`,
          { emailAddress: email }
        );
      });

    const termsAndConditionsType = this.oneTimePayFacade.getPaymentTermsAndConditionsType();

    this.oneTimePayFacade
      .getConfirmationObj()
      .pipe(take(1))
      .subscribe(confirmationObj => {
        this.subHeading = this.copyService.getCopy(
          `billing.simplifiedPayment.paymentConfirmationSubHeading`,
          { confirmationNumber: confirmationObj.paymentConfirmationId }
        );

        this.store.select(OneTimePaySelectors.selectOneTimePayEntities).pipe(
          tap({
            next: paymentEntity => {
              Object.entries(paymentEntity).forEach(([, paymentEntityValue]) => {
                const PaymentCompleteAnalytics = {
                  event: 'payment_complete',
                  payment_confirmation_id: confirmationObj.paymentConfirmationId,
                  payment_type:
                    termsAndConditionsType === PaymentTermsAndConditionsType.SCHEDULED_PAYMENT_CARD
                      ? 'credit card'
                      : 'bank transfer',
                  payment_term: paymentEntityValue.paymentTerm
                };
                this.analyticsFacade.trackEvent(PaymentCompleteAnalytics);
              });
              // For June Release, will come back and adjust once its figured out on dashboard side
              // const paymentComplete2 = {
              //   event: 'payment_complete',
              //   payment_confirmation_id: confirmationObj.paymentConfirmationId,
              //   number_of_billing_accounts: Object.entries(paymentEntity).length
              // };
              // this.analyticsFacade.trackEvent(paymentComplete2);
            }
          })
        );
      });
    this.simplifiedPaymentInfo$ = this.oneTimePayFacade.getPaymentReviewInfo();
    this.disclaimer$ = this.oneTimePayFacade.getPaymentConfirmationDisclaimerContent();
    this.spinner.stop();
    this.sendAnalytics();
    this.getCTAButtonNames();

    this.oneTimePayFacade
      .selectIsPhoenixPolicy()
      .pipe(
        map(data => data),
        takeUntil(this.stop$)
      )
      .subscribe(data => {
        this.isPhoenix = data;
      });
  }

  getCTAButtonNames() {
    this.oneTimePayFacade
      .getBillAccountsEligibleForAutoPay()
      .pipe(take(1))
      .subscribe(billAccounts => {
        if (billAccounts.length) {
          this.primaryButtonName = this.copyService.getCopy(
            'billing.simplifiedPayment.autoPayButton'
          );
          this.secondaryButtonName = this.copyService.getCopy(
            'billing.simplifiedPayment.overviewButton'
          );
        } else {
          this.primaryButtonName = this.copyService.getCopy(
            'billing.simplifiedPayment.overviewButton'
          );
        }
      });
  }

  print() {
    window.print();
    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: 'Simplified:ConfirmationPayment:Print'
    });

    // GA Analytics for payment confirmation print confirmation
    const printConfEvent: EventAnalyticParams = {
      event: 'print_confirmation',
      method: 'Payment Confirmation'
    };
    this.analyticsFacade.trackEvent(printConfEvent);
  }

  primaryEvent() {
    if (
      this.primaryButtonName === this.copyService.getCopy('billing.simplifiedPayment.autoPayButton')
    ) {
      this.oneTimePayFacade.goToAutoPaySetUp();
      this.oneTimePayFacade.sendAnalytics({
        buttonAnalyticName: 'Simplified:ConfirmationPayment:AutoPay:Start'
      });
    } else {
      this.secondaryEvent();
    }
  }

  secondaryEvent() {
    this.oneTimePayFacade.goToOverview();
    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: 'Simplified:ConfirmationPayment:BackToDashboard'
    });
  }

  sendAnalytics() {
    this.oneTimePayFacade.sendSimplifiedPaymentAnalytics();
    this.simplifiedPaymentInfo$.pipe(take(1)).subscribe(info => {
      if (info.selectedBillAccounts.length > 1) {
        setTimeout(() => {
          this.oneTimePayFacade.sendAnalytics({
            pageAnalyticName: 'Simplified:MultipleAccounts:Complete'
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
